<template>
  <div id="register" class="fill-height align-start">
    <template v-if="signupFlow !== 'NONE'">
      <v-row class="g-container" v-if="!loading" justify="center" no-gutters>
        <i-dialog-terms-conditions
          v-if="dialogTermsConditions"
          :dialog="dialogTermsConditions"
          @cancel="cancelDialogTermsConditions"
          @confirm="confirmDialogTermsConditions"
        />

        <i-dialog-privacy-policy
          v-if="dialogPrivacyPolicy"
          :dialog="dialogPrivacyPolicy"
          :loading="registering"
          @cancel="cancelDialogPrivacyPolicy"
          @confirm="register"
        />

        <v-col>
          <v-card
            v-if="registered"
            class="p-register-card mx-auto rounded-lg pa-4 pa-md-10 pb-0"
          >
            <v-row class="justify-center pb-2">
              <v-icon class="g-text-color-secondary" x-large
                >mdi-thumb-up</v-icon
              >
            </v-row>

            <v-row>
              <v-card-text class="text-center">
                <h2 class="text-center">Thank you for signing up!</h2>
                <div class="my-4">
                  <p>Thank you for your registration.</p>
                  <p>You should have received the registration email.</p>
                  <p>
                    Once your registration is approved, an email will be sent to
                    set up your password.
                  </p>
                  <p>
                    Once your password is set, you can log in and continue
                    trading.
                  </p>
                  <p v-if="supportEmail">
                    Any queries, contact
                    <a class="i-a-link" :href="`mailto:${supportEmail}`">{{
                      supportEmail
                    }}</a
                    >.
                  </p>
                </div>
                <p class="g-bold">You can now close this window.</p>
              </v-card-text>
            </v-row>
          </v-card>

          <template v-else>
            <v-card
              v-if="$vuetifyDisplay.smAndDown"
              class="p-message-card rounded-lg pa-2 mb-4"
            >
              <v-card-text class="text-center">
                <span
                  >It is recommended to complete your Sign up process on a
                  Desktop device</span
                >
              </v-card-text>
            </v-card>

            <v-card class="p-register-card mx-auto rounded-lg pa-4 pa-md-10">
              <v-card-title class="px-0 pt-0 pb-md-8">
                <h1 class="g-text-color-primary g-heading-l">Sign up</h1>
              </v-card-title>

              <v-card-subtitle class="px-0 pt-0 pb-2">
                <p>
                  If you already have an account you can click here to
                  <router-link class="i-a-link" :to="{ name: 'login' }"
                    >login</router-link
                  >.
                </p>
              </v-card-subtitle>

              <v-divider class="mt-4 mx-0" horizontal></v-divider>

              <v-card-text class="px-0 pt-0 pb-3 pb-md-6">
                <v-row no-gutters>
                  <span class="g-heading-s g-bold">Login & Security</span>
                  <v-spacer></v-spacer>
                  <v-icon v-if="loginAndSecurityForm" color="green" class="pb-1"
                    >mdi-check-circle-outline</v-icon
                  >
                  <v-icon v-else color="red" class="pb-1"
                    >mdi-alert-circle-outline</v-icon
                  >
                </v-row>
                <v-row no-gutters>
                  <v-col cols="12">
                    <v-alert
                      dense
                      text
                      outlined
                      border="left"
                      elevation="1"
                      type="info"
                    >
                      Email Address is required to Login.
                      <template v-if="signupFlow === 'APPROVAL'"
                        ><br />You will set your password once your registration
                        has been approved.</template
                      >
                    </v-alert>
                  </v-col>
                </v-row>
              </v-card-text>

              <v-form
                v-model="loginAndSecurityForm"
                :disabled="registering"
                @submit.prevent
              >
                <v-row>
                  <v-col class="py-0" cols="12" md="6">
                    <v-text-field
                      dense
                      hide-details="auto"
                      outlined
                      required
                      ref="emailField"
                      label="* Email Address (Required)"
                      maxlength="200"
                      background-color="#F0F0F0"
                      :value="email"
                      :loading="checkingEmail"
                      :rules="rulesEmail"
                      :error-messages="emailErrorMessage"
                      @input="(email) => checkEmailExistsDebounce(email)"
                    ></v-text-field>
                    <div
                      v-if="emailExists"
                      class="ml-3 mt-1 v-text-field__details"
                    >
                      <div class="v-messages error--text" role="alert">
                        <div class="v-messages__wrapper">
                          <div class="v-messages__message">
                            Email already registered -
                            <router-link
                              class="text-decoration-underline"
                              :to="{ name: 'login' }"
                              style="font-size: inherit"
                              >Login</router-link
                            >
                          </div>
                        </div>
                      </div>
                    </div>
                  </v-col>

                  <v-col class="py-0" cols="12" md="6">
                    <v-text-field
                      dense
                      outlined
                      required
                      background-color="#F0F0F0"
                      label="* Re-type Email Address (Required)"
                      maxlength="200"
                      v-model="reEmail"
                      :rules="[emailConfirmation]"
                    ></v-text-field>
                  </v-col>

                  <template v-if="signupFlow === 'NO_APPROVAL'">
                    <v-col class="py-0" cols="12" md="6">
                      <v-text-field
                        autocomplete="off"
                        dense
                        outlined
                        required
                        background-color="#F0F0F0"
                        label="* Password (Required)"
                        maxlength="50"
                        ref="passwordField"
                        v-model="password"
                        :rules="passwordRules"
                        :error-messages="passwordErrorMessage"
                        :type="showpassword ? 'text' : 'password'"
                        :append-icon="showpassword ? 'mdi-eye' : 'mdi-eye-off'"
                        @focus="
                          passwordValidator = Boolean(passwordErrorMessage)
                        "
                        @click:append="showpassword = !showpassword"
                      ></v-text-field>

                      <div v-if="passwordValidator" class="mb-4 ml-2" cols="12">
                        <i-password-criteria :password="password" />
                      </div>
                    </v-col>

                    <v-col class="py-0" cols="12" md="6">
                      <v-text-field
                        dense
                        v-model="repassword"
                        :rules="[passwordsMatchRule]"
                        type="password"
                        label="* Re-type Password (Required)"
                        maxlength="50"
                        outlined
                        background-color="#F0F0F0"
                        required
                      >
                      </v-text-field>
                    </v-col>
                  </template>
                </v-row>
                <v-row>
                  <v-col class="py-0" cols="12" md="6">
                    <v-text-field
                      dense
                      v-model="supplier.accountContact.firstName"
                      :rules="rulesFirstName"
                      label="* First Name (Required)"
                      maxlength="100"
                      outlined
                      background-color="#F0F0F0"
                      required
                    >
                    </v-text-field>
                  </v-col>

                  <v-col class="py-0" cols="12" md="6">
                    <v-text-field
                      dense
                      v-model="supplier.accountContact.lastName"
                      :rules="rulesLastName"
                      label="* Last Name (Required)"
                      maxlength="100"
                      outlined
                      background-color="#F0F0F0"
                      required
                    >
                    </v-text-field>
                  </v-col>

                  <v-col class="py-0" cols="12" md="6">
                    <v-text-field
                      dense
                      v-model="supplier.accountContact.mobile"
                      :rules="rulesPhone"
                      label="* Mobile or Landline (Required)"
                      maxlength="50"
                      hint="Add Area Code if Landline"
                      outlined
                      background-color="#F0F0F0"
                      required
                    >
                    </v-text-field>
                  </v-col>

                  <v-col class="py-0" cols="12" md="6">
                    <v-select
                      attach
                      background-color="#F0F0F0"
                      dense
                      :items="businessDepartments"
                      label="* Department (Required)"
                      outlined
                      required
                      :rules="[(v) => !!v || 'Department required']"
                      v-model="supplier.accountContact.role"
                    >
                    </v-select>
                  </v-col>
                </v-row>
              </v-form>

              <v-divider class="mx-0" horizontal></v-divider>

              <v-card-text class="px-0 pt-0 pb-3 pb-md-6">
                <v-row no-gutters>
                  <span class="g-heading-s g-bold">Business Details</span>
                  <v-spacer></v-spacer>
                  <v-icon v-if="businessDetailsForm" color="green" class="pb-1"
                    >mdi-check-circle-outline</v-icon
                  >
                  <v-icon v-else color="red" class="pb-1"
                    >mdi-alert-circle-outline</v-icon
                  >
                </v-row>
              </v-card-text>

              <v-form
                v-model="businessDetailsForm"
                :disabled="registering"
                @submit.prevent
              >
                <v-row>
                  <v-col class="py-0" cols="12" md="6">
                    <v-text-field
                      background-color="#F0F0F0"
                      dense
                      hint="This is your Supplier name in the Retailer Marketplace"
                      label="* Supplier Trading Name (Required)"
                      maxlength="100"
                      outlined
                      required
                      :rules="[
                        (v) => !!v || 'Supplier Trading Name required',
                        (v) => v.length <= 30 || 'Must be 30 characters or less'
                      ]"
                      v-model="supplier.name"
                    >
                    </v-text-field>
                  </v-col>

                  <v-col class="py-0" cols="12" md="6">
                    <v-text-field
                      dense
                      v-model="supplier.legalName"
                      label="* Business Name (Required)"
                      maxlength="100"
                      outlined
                      :rules="[(v) => !!v || 'Business Name required']"
                      background-color="#F0F0F0"
                      hint="This is your Business Legal Name"
                      required
                    >
                    </v-text-field>
                  </v-col>

                  <v-col class="py-0" cols="12" md="6">
                    <v-text-field
                      outlined
                      required
                      ref="abnField"
                      :label="businessNumberLabel"
                      :maxlength="isUsTenant ? 50 : 15"
                      :hint="businessNumberHint"
                      background-color="#F0F0F0"
                      dense
                      :value="supplier.abn"
                      :loading="checkingAbn"
                      :rules="businessNumberRules"
                      :error-messages="abnErrorMessage"
                      @focus="!!abnErrorMessage"
                      @input="(abn) => checkValidAbnDebounce(abn)"
                    ></v-text-field>
                  </v-col>

                  <v-col class="py-0" cols="12" md="6">
                    <v-text-field
                      v-model="supplier.acn"
                      :loading="checkingAbn"
                      :label="businessReferenceLabel"
                      background-color="#F0F0F0"
                      dense
                      :disabled="!isUsTenant"
                      outlined
                    ></v-text-field>
                  </v-col>

                  <v-col class="py-0" cols="12" md="6">
                    <v-text-field
                      v-model="supplier.businessContact.firstName"
                      label="* Business Contact First Name (Required)"
                      maxlength="100"
                      :rules="[
                        (v) => !!v || 'Business Contact First Name required'
                      ]"
                      outlined
                      background-color="#F0F0F0"
                      dense
                      hint="This is your Business Contact First Name"
                      required
                    >
                    </v-text-field>
                  </v-col>

                  <v-col class="py-0" cols="12" md="6">
                    <v-text-field
                      v-model="supplier.businessContact.lastName"
                      label="* Business Contact Last Name (Required)"
                      maxlength="100"
                      :rules="[
                        (v) => !!v || 'Business Contact Last Name required'
                      ]"
                      outlined
                      background-color="#F0F0F0"
                      dense
                      hint="This is your Business Contact Last Name"
                      required
                    >
                    </v-text-field>
                  </v-col>

                  <v-col class="py-0" cols="12" md="6">
                    <v-text-field
                      v-model="supplier.businessContact.email"
                      maxlength="200"
                      label="* Business Contact Email (Required)"
                      :rules="rulesEmail"
                      outlined
                      background-color="#F0F0F0"
                      dense
                      hint="This is your Business Contact Email"
                      required
                    >
                    </v-text-field>
                  </v-col>
                </v-row>
              </v-form>

              <v-divider class="mx-0" horizontal></v-divider>

              <v-card-text class="px-0 pt-0 pb-3 pb-md-6">
                <v-row no-gutters>
                  <span class="g-heading-s g-bold"
                    >Business / Billing Address</span
                  >
                  <v-spacer></v-spacer>
                  <v-icon
                    v-if="isValidBusinessAddress"
                    color="green"
                    class="pb-1"
                    >mdi-check-circle-outline</v-icon
                  >
                  <v-icon v-else color="red" class="pb-1"
                    >mdi-alert-circle-outline</v-icon
                  >
                </v-row>
              </v-card-text>

              <v-form
                v-model="businessAddressForm"
                :disabled="registering"
                @submit.prevent
              >
                <v-row>
                  <v-col class="py-0" cols="12">
                    <div class="i-address-finder-div">
                      <v-btn
                        text
                        class="i-btn-link-secondary px-2 ml-auto mt-n2 mt-md-n6"
                        @click="handleManualBillingAddress()"
                      >
                        {{
                          manualBillingAddress
                            ? 'Find Address'
                            : 'Cannot Find Address'
                        }}
                      </v-btn>
                    </div>

                    <template v-if="manualBillingAddress">
                      <v-text-field
                        v-model="supplier.billingAddress.line1"
                        outlined
                        required
                        background-color="#F0F0F0"
                        dense
                        label="* Street Number and Address (Required)"
                        maxlength="200"
                        :rules="[
                          (v) => !!v || 'Street Number and Address required'
                        ]"
                      ></v-text-field>
                    </template>

                    <template v-else>
                      <flewid-text-field
                        id="billingaddress"
                        :address-auto-suggest-country="
                          addressAutoSuggestCountryCode
                        "
                        :value="displayBillingAddress"
                        :rules="[
                          (v) => !!v || 'Street Number and Address required'
                        ]"
                        label="* Street Number and Address (Required)"
                        outlined
                        background-color="#F0F0F0"
                        required
                        enable-address-auto-suggest
                        @click="checkBusinessMobileExists"
                        @placechanged="onBillingAddressPlaceChanged"
                      />
                    </template>
                  </v-col>

                  <template v-if="manualBillingAddress">
                    <v-col class="py-0" cols="12" md="6">
                      <v-text-field
                        v-model="supplier.billingAddress.suburb"
                        :rules="[(v) => !!v || 'Suburb required']"
                        label="* Suburb (Required)"
                        maxlength="200"
                        outlined
                        background-color="#F0F0F0"
                        dense
                        required
                      >
                      </v-text-field>
                    </v-col>

                    <v-col class="py-0" cols="12" md="6">
                      <v-select
                        attach
                        background-color="#F0F0F0"
                        dense
                        :items="stateOptions"
                        label="* State (Required)"
                        maxlength="50"
                        outlined
                        required
                        :rules="[(v) => !!v || 'Delivery State required']"
                        v-model="supplier.billingAddress.state"
                      >
                      </v-select>
                    </v-col>

                    <v-col class="py-0" cols="12" md="6">
                      <v-text-field
                        v-model="supplier.billingAddress.postCode"
                        :rules="postcodeRules"
                        :label="postcodeLabel"
                        maxlength="10"
                        outlined
                        background-color="#F0F0F0"
                        dense
                        required
                      >
                      </v-text-field>
                    </v-col>

                    <v-col class="py-0" cols="12" md="6">
                      <v-text-field
                        v-model="supplier.billingAddress.country"
                        :rules="[(v) => !!v || 'Country required']"
                        label="* Country (Required)"
                        maxlength="50"
                        outlined
                        background-color="#F0F0F0"
                        dense
                        disabled
                        required
                      >
                      </v-text-field>
                    </v-col>
                  </template>

                  <v-col class="py-0" cols="12" md="6">
                    <v-text-field
                      v-model="supplier.billingAddress.mobile"
                      :rules="rulesPhone"
                      label="* Mobile or Landline (Required)"
                      maxlength="50"
                      hint="Add Area Code if Landline"
                      outlined
                      background-color="#F0F0F0"
                      dense
                      required
                      :error-messages="businessAddressMessage"
                      @click="businessAddressMessage = null"
                    >
                    </v-text-field>
                  </v-col>
                </v-row>
              </v-form>

              <v-divider class="mx-0" horizontal></v-divider>

              <v-card-text class="px-0 pt-0 pb-3 pb-md-6">
                <span class="g-heading-s g-bold">Licences</span>
              </v-card-text>

              <v-row>
                <v-col class="py-0" cols="12" md="6">
                  <v-text-field
                    outlined
                    background-color="#F0F0F0"
                    counter="30"
                    maxlength="30"
                    dense
                    label="Liquor Licence (Optional)"
                    v-model="supplier.liquorLicence"
                  >
                  </v-text-field>
                </v-col>
              </v-row>

              <v-divider class="mx-0" horizontal></v-divider>

              <v-alert
                border="left"
                color="red"
                dense
                dark
                v-if="!validSignUpForm"
                class="mb-8"
              >
                <span>All sections must have a green tick to Sign Up</span>
              </v-alert>

              <v-card-actions class="pa-0 justify-center justify-sm-end">
                <v-btn
                  large
                  rounded
                  class="i-btn-block-secondary p-btn"
                  :disabled="!validSignUpForm || registering"
                  :loading="registering"
                  v-on="{
                    click: initRegistrationFlow
                  }"
                >
                  Sign up
                  <v-icon>mdi-chevron-right</v-icon>
                </v-btn>
              </v-card-actions>
            </v-card>
          </template>
        </v-col>
      </v-row>
    </template>
    <template v-else>
      <i-the-role-blocker
        errorMessage="You are not allowed to access this page"
      />
    </template>
  </div>
</template>

<script>
import { defineComponent } from 'vue'

import Bugsnag from '@bugsnag/js'
import { mapActions, mapState, mapGetters } from 'vuex'
import BusinessDepartments from '@/utils/BusinessDepartments'
import FieldValidators from '@/utils/FieldValidators'
import iDialogPrivacyPolicy from '../components/DialogPrivacyPolicy.vue'
import iDialogTermsConditions from '../components/DialogTermsConditions.vue'
import {
  createSupplier,
  checkAbnValid,
  checkEmailExists
} from '@/services/profile'
import FlewidTextField from '@/components/FlewidTextField'
import iTheRoleBlocker from '@/components/TheRoleBlocker.vue'
import PasswordHelper from '@/components/helpers/PasswordHelper'
import iPasswordCriteria from '@/components/PasswordCriteria.vue'

const COUNTRY_REGION_AU = 'Australia'
const COUNTRY_REGION_US = 'US'
const COUNTRY_DISPLAY_NAME = {
  [COUNTRY_REGION_AU]: 'Australia',
  [COUNTRY_REGION_US]: 'USA'
}
const ADDRESS_AUTO_SUGGEST_COUNTRY = {
  [COUNTRY_REGION_AU]: 'au',
  [COUNTRY_REGION_US]: 'us'
}
const REGION_STATES = {
  [COUNTRY_REGION_AU]: ['ACT', 'NSW', 'NT', 'QLD', 'SA', 'TAS', 'VIC', 'WA'],
  [COUNTRY_REGION_US]: [
    'AL',
    'AK',
    'AZ',
    'AR',
    'CA',
    'CO',
    'CT',
    'DE',
    'DC',
    'FL',
    'GA',
    'HI',
    'ID',
    'IL',
    'IN',
    'IA',
    'KS',
    'KY',
    'LA',
    'ME',
    'MD',
    'MA',
    'MI',
    'MN',
    'MS',
    'MO',
    'MT',
    'NE',
    'NV',
    'NH',
    'NJ',
    'NM',
    'NY',
    'NC',
    'ND',
    'OH',
    'OK',
    'OR',
    'PA',
    'RI',
    'SC',
    'SD',
    'TN',
    'TX',
    'UT',
    'VT',
    'VA',
    'WA',
    'WV',
    'WI',
    'WY'
  ]
}

const DEBOUNCE_TIMER = 1000

const SIGNUP_FIELDS = {
  email: '',
  reEmail: '',
  password: '',
  repassword: '',

  supplier: {
    name: '',
    legalName: '',
    abn: '',
    acn: '',
    website: null,
    tobaccoLicence: '',
    liquorLicence: '',

    accountContact: {
      firstName: '',
      lastName: '',
      email: '',
      mobile: '',
      role: ''
    },

    businessContact: {
      firstName: '',
      lastName: '',
      email: '',
      mobile: null,
      role: null
    },

    billingAddress: {
      line1: '',
      suburb: '',
      state: '',
      postCode: '',
      country: COUNTRY_DISPLAY_NAME[COUNTRY_REGION_AU],
      mobile: ''
    },

    trading: true
  },
  termsConditions: false
}

export default defineComponent({
  components: {
    iDialogPrivacyPolicy,
    iDialogTermsConditions,
    FlewidTextField,
    iTheRoleBlocker,
    iPasswordCriteria
  },

  data: () => ({
    abnErrorMessage: null,
    businessAddressForm: false,
    businessAddressMessage: null,
    businessDepartments: BusinessDepartments,
    businessDetailsForm: false,
    checkingAbn: false,
    checkingEmail: false,
    debounceAbn: null,
    debounceEmail: null,
    dialogPrivacyPolicy: false,
    dialogTermsConditions: false,
    displayBillingAddress: null,
    emailErrorMessage: null,
    emailExists: false,
    loginAndSecurityForm: false,
    manualBillingAddress: false,
    passwordErrorMessage: null,
    passwordValidator: false,
    registering: false,
    registered: false,
    showpassword: false,
    ...FieldValidators,
    ...SIGNUP_FIELDS
  }),

  computed: {
    ...mapState('tenant', ['loading']),
    ...mapGetters('tenant', [
      'signupFlow',
      'isILR',
      'tenantCountryRegion',
      'termsConditionsEnabled',
      'privacyPolicyEnabled',
      'supportEmail'
    ]),

    isUsTenant() {
      return this.tenantCountryRegion === COUNTRY_REGION_US
    },

    addressAutoSuggestCountryCode() {
      return (
        ADDRESS_AUTO_SUGGEST_COUNTRY[this.tenantCountryRegion] ||
        ADDRESS_AUTO_SUGGEST_COUNTRY[COUNTRY_REGION_AU]
      )
    },

    businessNumberHint() {
      return this.isUsTenant
        ? 'Letters, numbers, spaces and hyphens allowed'
        : 'Between 11 & 14 characters (numbers and spaces only)'
    },

    businessNumberLabel() {
      return this.isUsTenant
        ? '* Business Registration Number (Required)'
        : '* Business ABN (Required)'
    },

    businessNumberRules() {
      if (!this.isUsTenant) return this.rulesBusinessABN
      return [
        (v) => !!v || 'Business Registration Number required',
        (v) =>
          /^[0-9a-zA-Z -]+$/.test(v) ||
          'Only letters, numbers, spaces and hyphens allowed',
        (v) => v?.length <= 50 || 'Must be 50 characters or less'
      ]
    },

    businessReferenceLabel() {
      return this.isUsTenant
        ? 'Business Reference (Optional)'
        : 'Business ACN (will auto fill if available)'
    },

    defaultedAccountContactEmail() {
      return this.supplier.accountContact.email || this.email
    },

    emailConfirmation() {
      return this.email === this.reEmail || 'Emails must match!'
    },

    isValidAbn() {
      const abn = this.supplier.abn
      if (this.isUsTenant) {
        return !!abn && /^[0-9a-zA-Z -]+$/.test(abn) && abn.length <= 50
      }
      return (
        !!abn && /^[0-9 ]+$/.test(abn) && abn.length >= 11 && abn.length <= 14
      )
    },

    isValidBusinessAddress() {
      return (
        this.businessAddressForm && // this makes sure all displayed fields are valid
        Object.values(this.supplier.billingAddress) // this makes sure all non-displayed fields are valid
          .every((value) => !!value)
      )
    },

    isValidEmail() {
      return !!this.email && /.+@.+/.test(this.email)
    },

    postcodeLabel() {
      return this.isUsTenant ? '* ZIP Code (Required)' : '* Postcode (Required)'
    },

    postcodeRules() {
      if (this.isUsTenant) {
        return [
          (v) => !!v || 'ZIP Code required',
          (v) => /^[0-9]{5}(-[0-9]{4})?$/.test(v) || 'Must be 5 digits or ZIP+4'
        ]
      }
      return this.rulesPostCode
    },

    passwordsMatchRule() {
      return PasswordHelper.passwordsMatchRule(this.password, this.repassword)
    },

    passwordRules() {
      return PasswordHelper.passwordRules()
    },

    stateOptions() {
      return (
        REGION_STATES[this.tenantCountryRegion] ||
        REGION_STATES[COUNTRY_REGION_AU]
      )
    },

    tenantCountryLabel() {
      return (
        COUNTRY_DISPLAY_NAME[this.tenantCountryRegion] ||
        COUNTRY_DISPLAY_NAME[COUNTRY_REGION_AU]
      )
    },

    validSignUpForm() {
      return (
        this.loginAndSecurityForm &&
        this.businessDetailsForm &&
        this.isValidBusinessAddress
      )
    }
  },

  watch: {
    tenantCountryRegion: {
      immediate: true,
      handler() {
        this.applyTenantCountryDefaults()
      }
    }
  },

  methods: {
    ...mapActions('notifications', ['addNotification']),

    // ***
    // Functions related to Auto and Manual addresses
    // ***

    applyTenantCountryDefaults() {
      this.supplier.billingAddress.country = this.tenantCountryLabel
    },

    checkBusinessMobileExists() {
      this.businessAddressMessage = this.supplier.billingAddress.mobile
        ? null
        : '* Store Mobile or Landline (required)'
    },

    createDisplayAddress(address = undefined) {
      if (address === undefined) return null
      return `${address.line1}, ${address.suburb}, ${address.state} ${address.postCode}`
    },

    handleManualBillingAddress() {
      this.manualBillingAddress = !this.manualBillingAddress
      if (!this.manualBillingAddress) {
        this.displayBillingAddress = null
        Object.assign(this.supplier.billingAddress, {
          line1: '',
          suburb: '',
          postCode: '',
          state: ''
        })
      }
    },

    onBillingAddressPlaceChanged(result) {
      this.displayBillingAddress = this.createDisplayAddress(result)

      Object.assign(this.supplier.billingAddress, {
        line1: result.line1,
        suburb: result.suburb,
        postCode: result.postCode,
        state: result.state,
        country: result.country || this.tenantCountryLabel
      })
    },

    // ***
    // Functions related to fields validation with BFF
    // ***

    checkValidAbnDebounce(abn) {
      this.supplier.abn = abn

      if (this.isUsTenant) {
        this.abnErrorMessage = null
        this.supplier.acn = this.supplier.acn || ''
        return
      }

      // Only validate in backend once has passed frontend validations
      if (this.isValidAbn) {
        clearTimeout(this.debounceAbn)
        this.debounceAbn = setTimeout(() => {
          this.checkingAbn = true
          this.abnErrorMessage = 'Validating ABN'

          checkAbnValid({ abn })
            .then((res) => {
              if (typeof res === 'object') {
                this.abnErrorMessage = null
                this.supplier.abn = res.abn
                this.supplier.acn = res.acn
              } else {
                this.abnErrorMessage = 'ABN is invalid'
                this.supplier.acn = ''
              }
            })
            .catch((err) => {
              // Validation APIs have request limiters
              const errMessage =
                err.response.status === 429
                  ? 'We detected too many ABN field validation attempts. Please wait a few minutes before trying again.'
                  : 'ABN validation not completed, try again...'

              this.addNotification({
                color: 'error',
                text: errMessage,
                timeout: -1
              })
            })
            .finally(() => {
              this.checkingAbn = false
            })
        }, DEBOUNCE_TIMER)
      }
    },

    checkEmailExistsDebounce(email) {
      this.email = email
      this.emailExists = false

      // Only validate in backend once has passed frontend validations
      if (this.isValidEmail) {
        clearTimeout(this.debounceEmail)
        this.debounceEmail = setTimeout(() => {
          this.checkingEmail = true
          this.emailErrorMessage = 'Validating Email'

          checkEmailExists({ email })
            .then((res) => {
              this.emailExists = res
              this.emailErrorMessage = null
            })
            .catch((err) => {
              // Validation APIs have request limiters
              const errMessage =
                err.response.status === 429
                  ? 'We detected too many Email field validation attempts. Please wait a few minutes before trying again.'
                  : 'Email validation not completed, try again...'

              this.addNotification({
                color: 'error',
                text: errMessage,
                timeout: -1
              })
            })
            .finally(() => {
              this.checkingEmail = false
            })
        }, DEBOUNCE_TIMER)
      }
    },

    // ***
    // Helper Functions
    // ***

    scrollTo(refName) {
      const element = this.$refs[refName]
      const offsetTop = element.offsetTop
      window.scrollTo(0, offsetTop)
    },

    // ***
    // Functions related to completing signup
    // ***

    initRegistrationFlow() {
      if (this.termsConditionsEnabled) {
        this.dialogTermsConditions = true
        return
      }

      if (this.privacyPolicyEnabled) {
        this.dialogPrivacyPolicy = true
        return
      }

      this.register()
    },

    initDialogTermsAndConditions() {
      this.dialogTermsConditions = true
    },

    cancelDialogTermsConditions() {
      this.dialogTermsConditions = false
    },

    confirmDialogTermsConditions() {
      this.dialogTermsConditions = false

      if (this.privacyPolicyEnabled) {
        this.dialogPrivacyPolicy = true
        return
      }

      this.register()
    },

    cancelDialogPrivacyPolicy() {
      this.dialogPrivacyPolicy = false

      if (this.termsConditionsEnabled) {
        this.dialogTermsConditions = true
      }
    },

    register() {
      this.registering = true

      const { email, password, supplier } = this

      supplier.accountContact.email = this.defaultedAccountContactEmail

      createSupplier({
        email,
        password,
        supplier: { ...supplier }
      })
        .then(() => {
          this.registered = true
          this.dialogTermsConditions = false
          this.dialogPrivacyPolicy = false
        })
        .catch((err) => {
          if (
            err.code === 'ECONNABORTED' ||
            /timeout/i.test(err.message || '')
          ) {
            this.registered = true
            this.dialogTermsConditions = false
            this.dialogPrivacyPolicy = false
            return
          }

          let error

          // Form submission API have request limiters
          if (err.response?.status === 429) {
            error =
              'We detected too many Form submissions. Please wait a few minutes before trying again.'
          } else {
            switch (err.response?.data?.code || 'UNKNOWN_ERROR') {
              case 'ABN_INVALID':
                error = this.abnErrorMessage = 'Incorrect ABN'
                this.scrollTo('abnField')
                break

              case 'EMAIL_EXISTS':
                error = 'Email already registered'
                this.emailExists = true
                this.scrollTo('emailField')
                break

              case 'PASSWORD_TOO_WEAK':
                error = this.passwordErrorMessage = 'Password is too weak'
                this.scrollTo('passwordField')
                break

              case 'PASSWORD_TOO_COMMON':
                error = this.passwordErrorMessage = 'Password is too common'
                this.scrollTo('passwordField')
                break

              case 'PASSWORD_CONTAINS_USER_INFO':
                error = this.passwordErrorMessage =
                  'Password contains some of your information'
                this.scrollTo('passwordField')
                break

              default:
                Bugsnag.notify(err)
                error = 'Something went wrong! Please try again...'
            }
          }

          this.addNotification({ color: 'error', text: error, timeout: -1 })
        })
        .finally(() => {
          this.registering = false
        })
    }
  }
})
</script>

<style lang="scss" scoped>
#register {
  hr {
    min-width: 100%;
    margin-top: 10px;
    margin-bottom: 30px;
  }
  .p-btn {
    width: 100%;
  }
  .p-message-card {
    max-width: 740px;
    background-color: $info-color;
    span {
      color: white !important;
    }
  }
  .p-register-card {
    max-width: 740px;
  }
}

@media only screen and (min-width: 600px) {
  #register {
    .p-btn {
      width: 160px;
    }
  }
}
</style>
